'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const credentials = [
{
  icon: '🎓',
  label: 'Licensed & Certified',
  detail: 'NAILS Certified Technician — NYC Board of Cosmetology'
},
{
  icon: '✨',
  label: '8 Years Experience',
  detail: 'Specializing in editorial and bridal nail art since 2016'
},
{
  icon: '🌿',
  label: 'Clean Beauty',
  detail: 'Exclusively using non-toxic, cruelty-free gel and acrylic systems'
},
{
  icon: '💍',
  label: 'Bridal Specialist',
  detail: 'Featured in Vogue Weddings and The Knot magazine'
}];


export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll('.reveal');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove('hidden-start');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 bg-surface" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-cream-dark rounded-5xl p-8 md:p-16 flex flex-col lg:flex-row gap-14 lg:gap-20 items-center overflow-hidden relative">
          {/* Decorative accent */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-accent/6 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-accent-rose/6 blur-3xl" />

          {/* Image */}
          <div className="lg:w-[42%] w-full flex-shrink-0 relative reveal hidden-start">
            <div className="relative rounded-4xl overflow-hidden aspect-[3/4] shadow-luxury">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_159e0c282-1766985572664.png"
                alt="Professional nail artist Sofia Reyes carefully applying intricate nail art design at her studio workstation"
                fill
                className="object-cover"
                quality={85} />
              
              {/* Floating badge */}
              <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-card">
                <p className="text-xs font-mono text-muted uppercase tracking-widest mb-1">
                  Currently based in
                </p>
                <p className="font-display text-lg font-semibold text-primary">New York City, NY</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {[1, 2, 3, 4, 5]?.map((i) =>
                  <svg key={i} className="w-3 h-3 text-accent fill-accent" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  )}
                  <span className="text-xs text-muted ml-1 font-medium">5.0 on Google</span>
                </div>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="lg:w-[58%] relative z-10">
            <span className="text-xs font-mono text-muted uppercase tracking-widest block mb-3 border-b border-border w-fit pb-1 reveal hidden-start">
              03 — About the Artist
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-primary tracking-tight leading-tight mb-6 reveal hidden-start reveal-delay-1">
              Precision meets
              <br />
              <span className="italic text-accent-rose">artistry</span>
            </h2>

            <div className="space-y-4 text-foreground-soft leading-relaxed mb-8 reveal hidden-start reveal-delay-2">
              <p>
                Hi, I'm <strong className="text-primary font-semibold">Sofia Reyes</strong> — a
                certified nail artist with over 8 years of experience turning nails into miniature
                canvases. My studio is a private, appointment-only space in Manhattan's West
                Village.
              </p>
              <p>
                I believe nail art is the most personal form of wearable art. Every client deserves
                a design that feels distinctly <em>theirs</em> — not pulled from a template, but
                crafted with intention and care.
              </p>
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 reveal hidden-start reveal-delay-3">
              {credentials?.map((cred, i) =>
              <div
                key={i}
                className="flex items-start gap-3 p-4 bg-surface rounded-2xl border border-border">
                
                  <span className="text-xl flex-shrink-0 mt-0.5">{cred?.icon}</span>
                  <div>
                    <p className="text-sm font-semibold text-primary mb-0.5">{cred?.label}</p>
                    <p className="text-xs text-muted leading-relaxed">{cred?.detail}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 reveal hidden-start reveal-delay-4">
              <a
                href="#booking"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white text-sm font-semibold rounded-full hover:bg-foreground-soft transition-all duration-300">
                
                Book with Sofia
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-border text-primary text-sm font-semibold rounded-full hover:bg-cream-dark transition-all duration-300">
                
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37m1.5-4.87h.01" />
                </svg>
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}